/**
 * Footprints in snow and sand - and the soldier who follows them (ROUND5 §4).
 *
 * The spec promised these in four places and the game had none of them at all:
 *   05 §7  the decal layer - "Footprints (arctic/desert), scorch marks, blood, tyre tracks
 *          and pod-landing scuffs are stamped into it. Footprints in snow are gameplay-
 *          relevant (enemy trackers can follow them)"
 *   03 §4  "Arctic: ... Noise x0.7; footprints persist"
 *   02 §9  the Dog team modifier - "Dog follows footprints/scent trail (last 60 s of your
 *          path) at walk pace ... Water breaks the trail"
 *   04 §8  the storm modifier - "vision -50 % both sides, heavy wind on drop, footprints
 *          vanish fast, stamina drain +20 %"
 *
 * ROUND8: the same trail is his SCENT, and scent is laid on every ground (ROUND8-contracts §1). A tracker dog reads
 * it anywhere (enemies/specials/dog.ts); a soldier still reads it only where it shows - snow and sand, `holdsTracks` -
 * and the renderer draws prints only there. Wading wipes it on any ground.
 *
 * The trail lives in `world.hazards.tracks`, so it is hashed with the world and a replay
 * reproduces it tick for tick (06 §R5). That also puts it in the suspend save and the cloud
 * save as JSON, which is why it is sampled by DISTANCE and capped hard - see `TRACKS`.
 *
 * `follow` is the point of the file: a trail nothing follows is decoration, and the spec's
 * claim would stay false. It is deliberately weak - it only redirects a man who is already
 * searching, it never touches his detection meter and it never raises the alert. Sight and
 * noise still decide everything; this only tells a hunter which way the quarry went.
 *
 * Its numbers live here rather than in `tuning.ts` because that file has no room left under
 * the 300-line limit; they belong to this module either way.
 */

import { type Ctx, isFighter } from '../ctx';
import { requestPath } from '../enemies/movement';
import { isAutomated } from '../enemies/specials/kinds';
import { waterAt } from '../terrainQueries';
import { secs } from '../tuning';
import type { Biome, MissionDef, SimMods, World } from '../types';
import { inStorm, type Sky } from '../weather';
import { rangerOnFoot } from './terrain';

export const TRACKS = {
  /**
   * Metres between prints - about two strides. Sampling by distance rather than by tick costs
   * a sprint and a stroll the same on disk, and a man standing still nothing at all.
   */
  spacing: 1.4,
  /**
   * Hard cap on samples kept: 96 x 1.4 m = 134 m of trail, which is 61 s at walking pace -
   * the "last 60 s of your path" of 02 §9, and about 3 KB of JSON. The world is serialised
   * into the suspend save and the cloud save (2 MB a value), so this is a cap and not a hint:
   * past it the oldest print is dropped, which is what ageing would have done to it anyway.
   * A man who SPRINTS for a minute covers more than this and loses the far end of his trail.
   */
  maxPoints: 96,
  /** 02 §9: the dog team follows "the last 60 s of your path". */
  lifeSeconds: 60,
  /** 04 §8, the storm: "footprints vanish fast" - the whole life divided by this. */
  stormFade: 3,
  /**
   * Light Step (04 §4): "snow footprints fade 50 % faster". The perk itself takes this figure
   * (`meta/content/perks.ts`) rather than keeping a second copy of it, and reaches the sim as
   * `SimMods.footprintFade` - so the words, the perk and the fade are one number.
   */
  lightStepFade: 1.5,
  /** How near a searching soldier must pass to cut the trail: he has to all but tread on it. */
  sniffRadius: 2.5,
  /** Older than this and it is a cold trail; he walks over it without reading it. */
  freshSeconds: 30,
  /** He reads this many prints ahead, then looks again: a follow, not a line drawn to the ranger. */
  followAhead: 3,
  /** One tick in this many a given soldier looks down, staggered by id so the cost is spread. */
  sniffEveryTicks: 15,
  /** Picking up a trail keeps him searching at least this long. */
  followSeconds: 6,
} as const;

/** Snow and sand take a print a man can see and follow; temperate ground does not (03 §4). A dog's nose needs none. */
export function holdsTracks(biome: Biome): boolean {
  return biome === 'arctic' || biome === 'desert';
}

/**
 * How long a print lasts, in ticks. What counts as a storm is `weather.ts`'s answer, not a
 * second hand-kept copy of it (ROUND5 §3.2 made that file the one place the sim asks).
 *
 * Light Step gets no `SimMods` field of its own and needs none: it is the only thing in the
 * game that touches `walkNoise`, so `walkNoise < 1` stood in for it - which would have broken
 * silently the day anything else quietened a walk. It has `SimMods.footprintFade` of its own now.
 */
export function trackLifeTicks(mods: SimMods, sky: Sky): number {
  const fade = (inStorm(sky) ? TRACKS.stormFade : 1) * Math.max(0.01, mods.footprintFade);
  return Math.max(1, Math.round(secs(TRACKS.lifeSeconds) / fade));
}

/** Centimetre resolution: a print is a decal, and the extra digits would only bloat the save. */
function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

/** Lay a print where the ranger is, if he has walked far enough since the last one. */
function lay(def: MissionDef, w: World): void {
  const r = w.ranger;
  const trail = w.hazards.tracks;
  if (!rangerOnFoot(w)) return;
  // 02 §9: "Water breaks the trail". Wading does not merely stop laying prints - it takes the
  // back-trail with it, so a river is a real counter to a hunter instead of a gap he steps over.
  if (waterAt(def, r.x, r.z)) {
    trail.length = 0;
    return;
  }
  if (!r.moving) return;
  const last = trail[trail.length - 1];
  if (last) {
    const dx = r.x - last.x;
    const dz = r.z - last.z;
    if (dx * dx + dz * dz < TRACKS.spacing * TRACKS.spacing) return;
  }
  trail.push({ x: round2(r.x), z: round2(r.z), t: w.tick });
  if (trail.length > TRACKS.maxPoints) trail.shift();
}

/** Drop the prints that have gone. They are laid oldest first, so only the head can expire. */
function age(w: World, life: number): void {
  const trail = w.hazards.tracks;
  let gone = 0;
  while (gone < trail.length && w.tick - (trail[gone]?.t ?? w.tick) >= life) gone++;
  if (gone > 0) trail.splice(0, gone);
}

/**
 * A searching soldier who cuts a fresh trail walks it toward its newest end (05 §7: "enemy
 * trackers can follow them"). He takes the nearest print he could tread on and makes for one a
 * few prints further along, then looks again - so he tracks at his own pace and loses it the
 * moment the trail fades out from under him. A man in an emplacement, a machine and a man out
 * of the fight never look down - reading the ground is a thing a soldier on his feet does.
 */
function follow(ctx: Ctx, w: World): void {
  const trail = w.hazards.tracks;
  if (trail.length < 2) return;
  const cold = secs(TRACKS.freshSeconds);
  const reach = TRACKS.sniffRadius * TRACKS.sniffRadius;
  for (const e of w.enemies) {
    if (e.state !== 'search' || e.mans !== null || isAutomated(e.kind) || !isFighter(e)) continue;
    if ((w.tick + e.id) % TRACKS.sniffEveryTicks !== 0) continue;
    let best = -1;
    let bestD = reach;
    for (let i = 0; i < trail.length; i++) {
      const p = trail[i];
      if (!p || w.tick - p.t > cold) continue;
      const dx = p.x - e.x;
      const dz = p.z - e.z;
      const d = dx * dx + dz * dz;
      if (d <= bestD) {
        bestD = d;
        best = i;
      }
    }
    if (best < 0) continue;
    const ahead = trail[Math.min(best + TRACKS.followAhead, trail.length - 1)];
    // Already making for that print: leave his path alone rather than rebuild it every quarter second.
    if (!ahead || (e.targetX === ahead.x && e.targetZ === ahead.z)) continue;
    e.targetX = ahead.x;
    e.targetZ = ahead.z;
    // The search state only walks a path it already has, so the path is asked for here.
    e.stateTicks = Math.max(e.stateTicks, secs(TRACKS.followSeconds));
    requestPath(ctx.statics, e, ahead.x, ahead.z);
  }
}

/** Runs with the other ground hazards, after the ranger has moved and before the garrison thinks. */
export function stepTracks(ctx: Ctx): void {
  const def = ctx.statics.def;
  const w = ctx.world;
  // ROUND8: laid and aged on every ground, for the dogs; followed by a soldier only where it shows.
  lay(def, w);
  age(w, trackLifeTicks(w.mods, def));
  if (holdsTracks(def.biome)) follow(ctx, w);
}
